'use strict'

import {
  CircularProgress,
  Tooltip,
  IconButton
} from '@material-ui/core'
import BarChartIcon from '@material-ui/icons/BarChart'
import CloseIcon from '@material-ui/icons/Close'
import WarningIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import FlashOnIcon from '@material-ui/icons/FlashOn'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { addDays, getWeekTypeForDate, getDayName, parseYmd } from './scheduleUtils'
import { formatMinutes } from './mapUtils'

const DAILY_OT_THRESHOLD_MIN = 480

/**
 * Desert Flora styled capacity details drawer.
 * Shows heatmap of the week OR detailed diagnostic view for a focused route.
 */
export default function CapacityDrawer ({
  capacityMatrix, capacityLoading,
  approvedRouteDays, approvedSet,
  focusRouteCapacityCell,
  // New props for diagnostic view
  focusedRoute, contextSummary, weeklyDisplayStats,
  hoursMode, setHoursMode,
  focusCrewName, focusDate,
  onClose
}) {
  // If we have a context summary, show the diagnostic view (Analyze mode)
  if (contextSummary) {
    return renderDiagnosticView()
  }

  if (!capacityMatrix?.routes?.length) return null

  // Fallback to Heatmap view
  return renderHeatmapView()

  function renderDiagnosticView() {
    const isOver = (contextSummary?.otMin || 0) > 0
    const stops = focusedRoute?.stops || []
    
    // Day-vs-threshold composition
    const dayUsedMin = contextSummary?.totalMin || 0
    const dayThresholdMin = DAILY_OT_THRESHOLD_MIN
    const dayMax = Math.max(dayUsedMin, dayThresholdMin, 1)
    const thresholdPct = (dayThresholdMin / dayMax) * 100
    
    const serviceMin = contextSummary?.serviceMin || 0
    const driveMin = contextSummary?.driveMin || 0

    // Per-stop Est vs Actual
    const stopRows = stops.map(s => {
      const estMin = Math.round(Number(s.serviceMinutes || 0))
      const actMin = s.medianServiceMinutes > 0 ? s.medianServiceMinutes : estMin
      const varMin = actMin - estMin
      const varPct = estMin > 0 ? Math.round((varMin / estMin) * 100) : 0
      return { ...s, estMin, actMin, varMin, varPct }
    })

    const totalEstService = stopRows.reduce((a, s) => a + s.estMin, 0)
    
    const worstStop = [...stopRows].sort((a, b) => b.varMin - a.varMin)[0]
    const worstNameRaw = worstStop?.propertyName || ''
    const worstName = worstNameRaw.includes(' | ') ? worstNameRaw.split(' | ')[1] : (worstNameRaw || 'a stop')

    // Suggested fix logic
    const lightestStop = [...stopRows].sort((a, b) => a.actMin - b.actMin)[0]
    const bestDay = weeklyDisplayStats?.days
      ? [...weeklyDisplayStats.days]
          .filter(d => d.date !== focusDate)
          .map(d => ({ ...d, headroom: DAILY_OT_THRESHOLD_MIN - d.displayTotal }))
          .sort((a, b) => b.headroom - a.headroom)[0]
      : null
    const canMove = bestDay && lightestStop && bestDay.headroom >= lightestStop.actMin

    return (
      <div className="cap-drawer" style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: 380, zIndex: 3000,
        background: 'var(--df-surface, #fbf8f1)',
        borderLeft: '1px solid var(--df-line, #e0d8c2)',
        boxShadow: '-8px 0 28px rgba(28,36,25,0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'df-drawerIn 0.26s cubic-bezier(.16,1,.3,1)'
      }}>
        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--df-line, #e0d8c2)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChartIcon style={{ fontSize: 18, color: 'var(--df-primary, #1f5e3a)' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink, #1c2419)', flex: 1 }}>
              Capacity Diagnosis
            </span>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <div className="seg-control" style={{ width: 180 }} onClick={(e) => e.stopPropagation()}>
              {['estimated', 'actual'].map(m => (
                <button
                  key={m}
                  className={'seg-btn' + (hoursMode === m ? ' active' : '')}
                  onClick={() => setHoursMode(m)}
                  style={{ padding: '4px 8px', fontSize: 11, textTransform: 'capitalize' }}
                >
                  {m}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 11, color: 'var(--df-ink-3, #6f7a6b)' }}>
              {focusCrewName || 'Crew'} · {getDayName(parseYmd(focusDate))?.slice(0, 3)}
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: 16 }}>
          {/* 1. Headline diagnosis */}
          {isOver ? (
            <div style={{ 
              background: '#fbeee6', border: '1px solid #e8c4a8', borderRadius: 10, 
              padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10 
            }}>
              <WarningIcon style={{ fontSize: 18, color: '#a4502f', flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#7a3115', lineHeight: 1.35 }}>
                  Day runs {formatMinutes(contextSummary.otMin)} over the {formatMinutes(dayThresholdMin)} threshold
                </div>
                <div style={{ fontSize: 11.5, color: '#a4502f', marginTop: 3, lineHeight: 1.4 }}>
                  {hoursMode === 'estimated' 
                    ? `Estimated service is too long for this day.`
                    : `Estimates predicted ${formatMinutes(totalEstService + driveMin)} (under threshold) — actual service ran longer, mostly at ${worstName}.`
                  }
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              background: '#e9f1e6', border: '1px solid #b6d4be', borderRadius: 10, 
              padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10 
            }}>
              <CheckCircleIcon style={{ fontSize: 18, color: 'var(--df-primary, #1f5e3a)', flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#164a2c', lineHeight: 1.35 }}>
                  Within capacity — {formatMinutes(dayThresholdMin - dayUsedMin)} of headroom
                </div>
                <div style={{ fontSize: 11.5, color: '#2f6b42', marginTop: 3, lineHeight: 1.4 }}>
                  Day totals {formatMinutes(dayUsedMin)} against an {formatMinutes(dayThresholdMin)} threshold. No action needed.
                </div>
              </div>
            </div>
          )}

          {/* 2. Day vs threshold composition */}
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4, #97a094)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Day vs threshold
          </div>
          <div style={{ position: 'relative', height: 16, marginBottom: 6 }}>
            {/* service + drive stacked */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', borderRadius: 6, overflow: 'hidden', background: 'var(--df-bg-2, #f1ece0)' }}>
              <div style={{ width: (serviceMin / dayMax * 100) + '%', background: isOver ? '#a9341d' : 'var(--df-primary, #1f5e3a)' }} />
              <div style={{ width: (driveMin / dayMax * 100) + '%', background: '#6e3fb7' }} />
            </div>
            {/* threshold marker */}
            <div style={{ position: 'absolute', top: -3, bottom: -3, left: thresholdPct + '%', width: 2, background: 'var(--df-ink, #1c2419)' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--df-ink-4, #97a094)', marginBottom: 10 }}>
            <span>0</span>
            <span style={{ color: 'var(--df-ink, #1c2419)', fontWeight: 600 }}>↑ Threshold {formatMinutes(dayThresholdMin)}</span>
            <span>{formatMinutes(dayMax)}</span>
          </div>
          <div style={{ display: 'flex', gap: 14, marginBottom: 4 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--df-ink-2, #3d463a)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: isOver ? '#a9341d' : 'var(--df-primary, #1f5e3a)' }} /> 
              Service {formatMinutes(serviceMin)}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--df-ink-2, #3d463a)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: '#6e3fb7' }} /> 
              Drive {formatMinutes(driveMin)}
            </span>
            {isOver && <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 700, color: '#a9341d' }}>{formatMinutes(contextSummary?.otMin || 0)} over</span>}
          </div>

          {/* 3. Per-stop Est vs Actual */}
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4, #97a094)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '18px 0 8px' }}>
            Stops — estimate vs actual
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {stopRows.map((s, i) => {
              const over = s.varMin > 0
              const barMax = Math.max(...stopRows.map(x => Math.max(x.estMin, x.actMin)), 1)
              return (
                <div key={s.id || s.aspirePropertyId} style={{ padding: '9px 0', borderBottom: i < stopRows.length - 1 ? '1px solid var(--df-line-soft, #ede6d2)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-ink, #1c2419)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.propertyName?.split(' | ')[1] || s.propertyName}
                    </span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: over ? '#a9341d' : '#2f7a3e', flexShrink: 0 }}>
                      {over ? '+' : ''}{formatMinutes(s.varMin)} <span style={{ color: 'var(--df-ink-4, #97a094)', fontWeight: 500 }}>({over ? '+' : ''}{s.varPct}%)</span>
                    </span>
                  </div>
                  <div style={{ position: 'relative', height: 8 }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: (s.estMin / barMax * 100) + '%', background: '#c7bfa8', borderRadius: 4 }} title={'Est ' + formatMinutes(s.estMin)} />
                    <div style={{ position: 'absolute', left: 0, top: 1.5, height: 5, width: (s.actMin / barMax * 100) + '%', background: over ? '#a9341d' : '#2f7a3e', borderRadius: 4 }} title={'Act ' + formatMinutes(s.actMin)} />
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--df-ink-4, #97a094)' }}>
              <span style={{ width: 16, height: 5, borderRadius: 3, background: '#c7bfa8' }} /> Estimate
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--df-ink-4, #97a094)' }}>
              <span style={{ width: 16, height: 5, borderRadius: 3, background: '#a9341d' }} /> Actual
            </span>
          </div>

          {/* 4. Rebalance suggestion */}
          {isOver && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4, #97a094)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '18px 0 8px' }}>
                Suggested fix
              </div>
              <div style={{ background: '#e9f1e6', border: '1px solid #b6d4be', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', gap: 9 }}>
                  <FlashOnIcon style={{ fontSize: 18, color: 'var(--df-primary, #1f5e3a)', flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1 }}>
                    {canMove ? (
                      <>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#164a2c', lineHeight: 1.35 }}>Move a stop to {bestDay.dayName}</div>
                        <div style={{ fontSize: 11.5, color: '#2f6b42', marginTop: 3, lineHeight: 1.45 }}>
                          {bestDay.dayName} sits at {formatMinutes(bestDay.displayTotal)} ({Math.round(bestDay.displayTotal / dayThresholdMin * 100)}% of capacity) with <strong>{formatMinutes(bestDay.headroom)}</strong> headroom — enough to absorb {worstName.length > 22 ? worstName.slice(0,22)+'…' : worstName} and clear most of today's OT.
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#164a2c', lineHeight: 1.35 }}>Reassign {worstName} to a neighboring crew</div>
                        <div style={{ fontSize: 11.5, color: '#2f6b42', marginTop: 3, lineHeight: 1.45 }}>
                          Every other day this week is already near or over capacity, so there's no room to shuffle internally. {worstName} alone ran <strong>{formatMinutes(worstStop?.actMin || 0)}</strong> ({worstStop?.varMin > 0 ? '+' : ''}{formatMinutes(worstStop?.varMin || 0)} over estimate) — moving it off this route clears most of the overage.
                        </div>
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                      <button className="btn btn-primary btn-sm" style={{ gap: 4 }}>
                        <FlashOnIcon style={{ fontSize: 14 }} /> {canMove ? 'Apply suggestion' : 'Find a crew'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => {}}>Dismiss</button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Toggle back to heatmap if matrix available */}
          {capacityMatrix?.routes?.length > 0 && (
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => focusRouteCapacityCell(null, -1)}
                style={{ color: 'var(--df-primary, #1f5e3a)', fontWeight: 700 }}
              >
                View all routes heatmap <ChevronRightIcon style={{ fontSize: 16 }} />
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  function renderHeatmapView() {
    return (
      <div className="cap-drawer" style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: 380, zIndex: 3000,
        background: 'var(--df-surface, #fbf8f1)',
        borderLeft: '1px solid var(--df-line, #e0d8c2)',
        boxShadow: '-8px 0 28px rgba(28,36,25,0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'df-drawerIn 0.26s cubic-bezier(.16,1,.3,1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--df-line, #e0d8c2)',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChartIcon style={{ fontSize: 18, color: 'var(--df-primary, #1f5e3a)' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink, #1c2419)', flex: 1 }}>
              Route Capacity (week of {capacityMatrix.weekOf || '?'})
            </span>
            {capacityLoading && <CircularProgress size={14} style={{ color: 'var(--df-primary, #1f5e3a)' }} />}
            {approvedRouteDays.length > 0 && (
              <span style={{ fontSize: 11, color: '#2f7a3e', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {approvedRouteDays.length} approved
              </span>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: 16 }}>
          {/* Heatmap table */}
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 12
          }}>
            <thead>
              <tr>
                <th style={{
                  textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4, #97a094)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  padding: '6px 4px', borderBottom: '1px solid var(--df-line, #ede6d2)'
                }}>Route</th>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(d => (
                  <th key={d} style={{
                    textAlign: 'center', fontSize: 10, fontWeight: 700,
                    color: 'var(--df-ink-4, #97a094)', textTransform: 'uppercase',
                    letterSpacing: '0.04em', padding: '6px 2px',
                    borderBottom: '1px solid var(--df-line, #ede6d2)'
                  }}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {capacityMatrix.routes.map(route => {
                const days = route.days || {}
                const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
                return (
                  <tr key={route.routeId}>
                    <td style={{
                      textAlign: 'left', fontWeight: 500, maxWidth: 100,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      paddingRight: 4, padding: '4px 4px', fontSize: 11.5,
                      color: 'var(--df-ink-2, #3d463a)'
                    }}>
                      <Tooltip title={`${route.routeName}${route.distanceMiles != null ? ` (${route.distanceMiles} mi)` : ''}`} arrow>
                        <span>{route.routeName?.replace(/\s*\(.*\)/, '') || route.routeId}</span>
                      </Tooltip>
                    </td>
                    {dayOrder.map((dayName, dayIdx) => {
                      const dm = days[dayName] || {}
                      const pct = dm.capacityPct || 0
                      const dotColor = pct > 85 ? '#a9341d' : pct > 60 ? '#b35d1a' : '#2f7a3e'
                      const cellWeekType = capacityMatrix.weekOf
                        ? getWeekTypeForDate(addDays(new Date(capacityMatrix.weekOf + 'T00:00:00'), dayIdx))
                        : ''
                      const isApproved = approvedSet.has(`${route.crewId}_${dayName}_${cellWeekType}`)
                      return (
                        <td
                          key={dayName}
                          style={{
                            textAlign: 'center', padding: '4px 2px',
                            cursor: 'pointer'
                          }}
                          onClick={() => focusRouteCapacityCell(route, dayIdx)}
                        >
                          <Tooltip
                            title={`${dm.stops || 0} stops · ${formatMinutes(dm.totalMin || 0)} / 8h (${Math.round(pct)}%)${isApproved ? ` · Approved (Wk ${cellWeekType})` : ''}`}
                            arrow
                          >
                            <span style={{
                              display: 'inline-block', width: 14, height: 14,
                              borderRadius: '50%', verticalAlign: 'middle',
                              backgroundColor: dotColor,
                              ...(isApproved && {
                                border: '2px solid #2f7a3e',
                                boxShadow: '0 0 0 1px #fff'
                              })
                            }} />
                          </Tooltip>
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }
}
